'use client';

import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { findKnnColumnIndex, getKnnRowSourceType } from '@/lib/knnResponse';

ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

interface Parameter {
    name: string;
    selected: boolean;
}

interface SpiderChartProps {
    data?: any;
    controls?: boolean;
    chartWidth?: number;
    chartHeight?: number;
    itemPerCaseForTestCast?: number;
}

// These match the Angular implementation exactly
const reqColumnnames = [
    'Volume',
    'FilledWeight',
    'Bottle_Diameter',
    'Bottle_Height',
    'Cases/Layer',
    'Layers/Pallet',
    'Items/Case',
    'TotalCases/Pallet',
    'Items/Pallet',
    'FTL',
    'MaxPalletStacked',
    'CaseLength',
    'CaseWidth',
    'CaseHeight',
    'Over/UnderHang'
];
const preChecked = ["Volume", "FilledWeight", "Bottle_Diameter", "Bottle_Height", "Items/Case", "CaseLength", "CaseWidth", "CaseHeight",]

const legacyColumnIndexes = [3, 4, 5, 6, 11, 12, 13, 14, 15, 16, 17, 20, 21, 22, 36];

const spiderChartColumnAliases: Record<string, string[]> = {
    Volume: ['Volume(ml)', 'Volume'],
    FilledWeight: ['FilledWeight(g)', 'Filled Weight(g)', 'Filled Weight'],
    Bottle_Diameter: ['Diameter(in)', 'Bottle Diameter(in)', 'Bottle Diameter', 'Diameter'],
    Bottle_Height: ['Height(in)', 'Bottle Height(in)', 'Bottle Height', 'Height'],
    'Cases/Layer': ['Cases Per Layer', 'Cases/Layer'],
    'Layers/Pallet': ['Layers/Pallet', 'Layers Per Pallet'],
    'Items/Case': ['Items/Case', 'Items Per Case'],
    'TotalCases/Pallet': ['Total Cases Per Pallet', 'TotalCases/Pallet'],
    'Items/Pallet': ['Items/pallet', 'Items/Pallet', 'Items Per Pallet'],
    FTL: ['StretchWrap FTL(lbs)', 'FTL'],
    MaxPalletStacked: ['Max Pallets Stacked', 'MaxPalletStacked'],
    CaseLength: ['CaseLength(in)', 'Case Length(in)', 'Case Length'],
    CaseWidth: ['CaseWidth(in)', 'Case Width(in)', 'Case Width'],
    CaseHeight: ['CaseHeight(in)', 'Case Height(in)', 'Case Height'],
    'Over/UnderHang': ['overhang/underhang(mm)', 'Over/UnderHang', 'OverhangOverNo'],
};

export const resolveSpiderChartColumnIndexes = (columns: unknown[] = []) => (
    reqColumnnames.map((parameterName, parameterIndex) => {
        const resolvedIndex = findKnnColumnIndex(columns, spiderChartColumnAliases[parameterName] || [parameterName]);
        if (resolvedIndex >= 0) return resolvedIndex;
        return columns.length === 0 ? legacyColumnIndexes[parameterIndex] : -1;
    })
);

// Normalization function for TestCase Items/Case
const normalizeTestCaseItemsPerCase = (value: number): number => {
    const Xmin = 4;
    const Xmax = 48;
    // X_scaled = 0.1 + ((X - Xmin)/(Xmax - Xmin)) * (0.9 - 0.1)
    return 0.1 + ((value - Xmin) / (Xmax - Xmin)) * (0.9 - 0.1);
};

export const toSpiderChartNumber = (value: unknown): number => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

export const selectSpiderChartRowIndexes = (rows: unknown[][] = [], columns: unknown[] = []) => {
    const physicalOptions = new Set<string>();

    return rows.reduce<number[]>((indexes, row, index) => {
        const optionNo = String(row?.[0] ?? '').trim();
        const sourceType = getKnnRowSourceType(row, columns, optionNo);

        if (sourceType === 'physical') {
            const physicalOptionKey = optionNo.toLowerCase();
            if (physicalOptions.has(physicalOptionKey)) return indexes;
            physicalOptions.add(physicalOptionKey);
        }

        indexes.push(index);
        return indexes;
    }, []);
};

// Unit mapping for each parameter
const parameterUnits: Record<string, string> = {
    'Volume': 'oz',
    'FilledWeight': 'lbs',
    'Bottle_Diameter': 'in',
    'Bottle_Height': 'in',
    'Cases/Layer': 'cases',
    'Layers/Pallet': 'layers',
    'Items/Case': 'items',
    'TotalCases/Pallet': 'cases',
    'Items/Pallet': 'items',
    'FTL': 'lbs',
    'MaxPalletStacked': 'pallets',
    'CaseLength': 'in',
    'CaseWidth': 'in',
    'CaseHeight': 'in',
    'Over/UnderHang': 'mm'
};

// Conversion functions for display
const convertValueForDisplay = (paramName: string, value: number): number => {
    switch (paramName) {
        case 'Volume':
            // Convert ml to fluid ounces: 1 ml = 0.033814 fl oz
            return Number((value * 0.033814).toFixed(1));
        case 'FilledWeight':
            // Convert grams to pounds: 1 gram = 0.00220462 lbs
            return Math.round((value * 0.00220462) * 100) / 100;
        case 'Over/UnderHang':
            // Show actual mm values on hover (no conversion)
            return Math.round(value * 100) / 100;
        default:
            return Math.round(value * 100) / 100;
    }
};

// Exact color lists from Angular
const colorList = [
    'rgba(0,0,0,0.2)',
    'rgba(255,255,255,0.2)',
    'rgba(255,0,0,0.2)',
    'rgba(0,255,0,0.2)',
    'rgba(0,0,255,0.2)',
    'rgba(255,255,0,0.2)',
    'rgba(0,255,255,0.2)',
    'rgba(255,0,255,0.2)',
    'rgba(192,192,192,0.2)',
    'rgba(128,128,128,0.2)',
    'rgba(128,0,0,0.2)',
    'rgba(128,128,0,0.2)',
    'rgba(0,128,0,0.2)',
    'rgba(128,0,128,0.2)',
    'rgba(0,128,128,0.2)',
    'rgba(0,0,128,0.2)'
];

const colorListLines = [
    'rgb(0,0,0)',
    'rgb(205,205,185)',
    'rgb(255,0,0)',
    'rgb(0,255,0)',
    'rgb(0,0,255)',
    'rgb(255,255,0)',
    'rgb(0,255,255)',
    'rgb(255,0,255)',
    'rgb(192,192,192)',
    'rgb(128,128,128)',
    'rgb(128,0,0)',
    'rgb(128,128,0)',
    'rgb(0,128,0)',
    'rgb(128,0,128)',
    'rgb(0,128,128)',
    'rgb(0,0,128)'
];

interface SpiderChartRef {
    getChartImage: () => string | null;
}

const SpiderChart = forwardRef<SpiderChartRef, SpiderChartProps>(({
    data,
    controls = true,
    chartWidth = 400,
    chartHeight = 400,
    itemPerCaseForTestCast
}, ref) => {
    const chartRef = useRef<any>(null);
    const [parameters, setParameters] = useState<Parameter[]>([]);
    const [chartData, setChartData] = useState<any>({
        labels: [],
        datasets: []
    });
    const [sourceDatasets, setSourceDatasets] = useState<any[]>([]);
    const [originalRawValues, setOriginalRawValues] = useState<any[][]>([]);

    // Expose method to get chart as image
    useImperativeHandle(ref, () => ({
        getChartImage: () => {
            if (chartRef.current) {
                return chartRef.current.toBase64Image('image/png', 1.0);
            }
            return null;
        }
    }));

    useEffect(() => {
        // Initialize parameters with first 6 selected by default
        const initialParameters = reqColumnnames.map((name) => ({
            name,
            selected: preChecked.includes(name)
        }));
        setParameters(initialParameters);

        // Process data if available
        if (data?.data && data?.data_2) {
            processData(data);
        }
    }, [data, itemPerCaseForTestCast]);

    // Modify the processData function to ensure it immediately updates the chart
    const processData = (sourceData: any) => {
        if (!sourceData?.data_2 || !sourceData?.data) return;

        // Create deep copies of the arrays to avoid read-only issues
        const includedRowIndexes = selectSpiderChartRowIndexes(sourceData.data, sourceData.columns || []);
        const dataArray = includedRowIndexes.map(index => [...(sourceData.data_2[index] || [])]); // Normalized data for chart rendering
        const rawDataArray = includedRowIndexes.map(index => [...(sourceData.data[index] || [])]); // Raw data for tooltips

        const chartColumnIndexes = resolveSpiderChartColumnIndexes(sourceData.columns || []);
        const itemsPerCaseColumnIndex = chartColumnIndexes[reqColumnnames.indexOf('Items/Case')];

        // Update TestCase Items/Case if itemPerCaseForTestCast is provided
        if (itemPerCaseForTestCast !== undefined) {
            const testCaseIndex = rawDataArray.findIndex(row => String(row[0] ?? '').toLowerCase() === 'testcase');
            if (testCaseIndex !== -1 && itemsPerCaseColumnIndex !== undefined && itemsPerCaseColumnIndex >= 0) {
                rawDataArray[testCaseIndex][itemsPerCaseColumnIndex] = itemPerCaseForTestCast;

                // Update normalized data with the normalized value
                const normalizedValue = normalizeTestCaseItemsPerCase(itemPerCaseForTestCast);
                dataArray[testCaseIndex][itemsPerCaseColumnIndex] = normalizedValue;
            }
        }

        const dataSets = [];
        const rawValuesStorage = [];

        // Create datasets for each row in the data
        for (let i = 0; i < dataArray.length; i++) {
            const dArray = dataArray[i];
            const rawDArray = rawDataArray[i];
            const subArray = chartColumnIndexes.map(index => toSpiderChartNumber(dArray[index]));

            // Store the original raw values for tooltip display from raw data
            const rawSubArray = chartColumnIndexes.map(index => rawDArray[index]);
            rawValuesStorage.push(rawSubArray);

            const dataset = {
                data: subArray,
                label: dataArray[i][0].toString(),
                fill: true,
                backgroundColor: colorList[i % colorList.length],
                borderColor: colorListLines[i % colorListLines.length],
                pointBackgroundColor: colorListLines[i % colorListLines.length],
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: colorListLines[i % colorListLines.length]
            };

            dataSets.push(dataset);
        }

        setSourceDatasets(dataSets);
        setOriginalRawValues(rawValuesStorage);

        // Immediately update chart with the initial parameters
        const initialSelectedParams = preChecked
        const indices = initialSelectedParams.map(item => reqColumnnames.indexOf(item));

        const initialChartDatasets = [];
        for (let i = 0; i < dataSets.length; i++) {
            const dArray = dataSets[i].data;
            const subArray = indices.map(index => toSpiderChartNumber(dArray[index]));

            const dataset = {
                data: subArray,
                label: dataSets[i].label,
                fill: true,
                backgroundColor: colorList[i % colorList.length],
                borderColor: colorListLines[i % colorListLines.length],
                pointBackgroundColor: colorListLines[i % colorListLines.length],
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: colorListLines[i % colorListLines.length]
            };

            initialChartDatasets.push(dataset);
        }

        setChartData({
            labels: initialSelectedParams,
            datasets: initialChartDatasets
        });
    };

    const handleParameterChange = (paramName: string, checked: boolean) => {
        const updatedParams = parameters.map(p =>
            p.name === paramName ? { ...p, selected: checked } : p
        );
        setParameters(updatedParams);
        updateChartData(sourceDatasets, updatedParams);
    };

    const selectAll = () => {
        const updatedParams = parameters.map(p => ({ ...p, selected: true }));
        setParameters(updatedParams);
        updateChartData(sourceDatasets, updatedParams);
    };

    const deselectAll = () => {
        const updatedParams = parameters.map(p => ({ ...p, selected: false }));
        setParameters(updatedParams);
        updateChartData(sourceDatasets, updatedParams);
    };

    const updateChartData = (sourceSets: any[] = sourceDatasets, params: Parameter[] = parameters) => {
        const selectedParameters = params.filter(param => param.selected);
        const reqParams = selectedParameters.map(parameter => parameter.name);
        const indices = reqParams.map(item => reqColumnnames.indexOf(item));

        const dataSets2 = [];

        for (let i = 0; i < sourceSets.length; i++) {
            const dArray = sourceSets[i].data;
            const subArray = indices.map(index => toSpiderChartNumber(dArray[index]));

            const dataset = {
                data: subArray,
                label: sourceSets[i].label,
                fill: true,
                backgroundColor: colorList[i % colorList.length],
                borderColor: colorListLines[i % colorListLines.length],
                pointBackgroundColor: colorListLines[i % colorListLines.length],
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: colorListLines[i % colorListLines.length]
            };

            dataSets2.push(dataset);
        }

        setChartData({
            labels: reqParams,
            datasets: dataSets2
        });
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false, // Important for resizing
        plugins: {
            tooltip: {
                callbacks: {
                    title: (tooltipItems: any[]) => {
                        if (tooltipItems.length > 0) {
                            const dataIndex = tooltipItems[0].dataIndex;
                            const paramName = chartData.labels[dataIndex];
                            const unit = parameterUnits[paramName] || '';
                            return `${paramName}${unit ? ` (${unit})` : ''}`;
                        }
                        return '';
                    },
                    label: (tooltipItem: any) => {
                        const datasetIndex = tooltipItem.datasetIndex;
                        const dataIndex = tooltipItem.dataIndex;
                        const paramName = chartData.labels[dataIndex];

                        // Get the original raw value instead of normalized value
                        if (originalRawValues[datasetIndex]) {
                            const selectedParamIndices = chartData.labels.map(label => reqColumnnames.indexOf(label));
                            const originalParamIndex = selectedParamIndices[dataIndex];
                            const originalValue = originalRawValues[datasetIndex][originalParamIndex];
                            const convertedValue = convertValueForDisplay(paramName, originalValue);
                            return `${tooltipItem.dataset.label}: ${convertedValue}`;
                        }

                        // Fallback to raw value if original values not available
                        const value = tooltipItem.raw;
                        const convertedValue = convertValueForDisplay(paramName, value);
                        return `${tooltipItem.dataset.label}: ${convertedValue}`;
                    },
                },
            },
            legend: {
                position: 'bottom' as const,
                labels: {
                    font: {
                        size: 16,
                    },
                },
            },
        },
        scales: {
            r: {
                beginAtZero: true,
                pointLabels: {
                    font: {
                        size: 16,
                    },
                },
                angleLines: {
                    display: true,
                },
                grid: {
                    circular: true,
                },
                ticks: {
                    stepSize: 0.1,
                },
            },
        },
    };

    // Adjust size based on the chart dimensions provided
    const chartFrameHeight = Math.max(420, Math.min(chartHeight * 0.8, chartWidth * 1.3));
    const chartContainerStyle = {
        width: `${chartWidth}px`,
        height: `${chartFrameHeight}px`,
        maxWidth: '100%',
        display: 'flex',
        // justifyContent: 'center',
        alignItems: 'center'
    };

    const radarStyle = {
        width: '100%',
        height: '100%',
        maxHeight: '1200px' // Ensure chart doesn't get too large
    };

    const containerStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        width: '100%',
        height: '100%'
    };

    const controlsStyle = {
        width: '100%',
        height: `${chartHeight * 0.2}px`, // Reduced to 20% for controls
        maxHeight: '300px', // Cap controls height
        overflow: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '0.375rem',
        marginTop: '0.5rem',
        padding: '0.75rem',
        backgroundColor: 'white'
    };

    // Determine the number of columns based on width - allow for more columns on wider charts
    const columnCount = chartWidth < 400 ? 2 :
        chartWidth < 600 ? 3 :
            chartWidth < 800 ? 4 :
                chartWidth < 1000 ? 5 : 6;

    return (
        <div style={containerStyle}>
            <div style={chartContainerStyle}>
                <div style={radarStyle}>
                    <Radar
                        ref={chartRef}
                        data={chartData}
                        options={options}
                    />
                </div>
            </div>
            {controls && (
                <div style={controlsStyle}>
                    <div className="mb-1">
                        <div className="flex justify-between items-center">
                            <h4 className="font-medium text-sm">Parameters</h4>
                            <div className="flex gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={selectAll}
                                    className="text-xs h-6 px-2"
                                >
                                    Select All
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={deselectAll}
                                    className="text-xs h-6 px-2"
                                >
                                    Clear All
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}>
                        {parameters.map((param) => (
                            <div key={param.name} className="flex items-center space-x-1">
                                <Checkbox
                                    checked={param.selected}
                                    onCheckedChange={(checked) =>
                                        handleParameterChange(param.name, checked as boolean)
                                    }
                                    id={param.name}
                                    className="h-3 w-3"
                                />
                                <Label htmlFor={param.name} className="text-xs truncate">{param.name}</Label>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});

SpiderChart.displayName = 'SpiderChart';

export default SpiderChart;
export type { SpiderChartRef }; 
